import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * OptionCard: one choice among a few, with a sentence about each (CO-88,
 * audit F3). The badge sits under the title, never beside it, so titles
 * are not squeezed onto several lines; `OptionGroup` lays out at most three
 * per row, never narrower than 240 px.
 *
 * A card is a button that is pressed when selected, or a link when each
 * option is its own page (the platform choice).
 */
export function OptionGroup({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div className="option-group" role="group" aria-label={label} data-testid={testId}>
      {children}
    </div>
  );
}

interface OptionCardProps {
  title: ReactNode;
  description?: ReactNode;
  /** A status or recommendation, shown under the title. */
  badge?: ReactNode;
  /** A consequence or caveat, under the description. */
  note?: ReactNode;
  noteTone?: 'warning' | 'danger';
  /** Words shown on the selected card ("Selected for this plan"), so the
   * choice is not marked by colour alone. */
  selectedLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  testId?: string;
}

function Content({ title, description, badge, note, noteTone, selected, selectedLabel }: OptionCardProps) {
  return (
    <>
      <span className="choice-card__title">{title}</span>
      {badge ? <span className="option-card__badge">{badge}</span> : null}
      {description ? <span className="choice-card__desc">{description}</span> : null}
      {note ? <span className={`option-card__note${noteTone ? ` option-card__note--${noteTone}` : ''}`}>{note}</span> : null}
      {selected && selectedLabel ? <span className="choice-card__tag">{selectedLabel}</span> : null}
    </>
  );
}

export function OptionCard(props: OptionCardProps & ({ onSelect: () => void; to?: never } | { to: string; onSelect?: never })) {
  const className = `choice-card option-card${props.selected ? ' choice-card--selected' : ''}`;
  if (props.to !== undefined) {
    return (
      <Link to={props.to} className={className} aria-current={props.selected ? 'page' : undefined} data-testid={props.testId}>
        <Content {...props} />
      </Link>
    );
  }
  return (
    <button type="button" className={className} aria-pressed={Boolean(props.selected)} disabled={props.disabled} onClick={props.onSelect} data-testid={props.testId}>
      <Content {...props} />
    </button>
  );
}
